import api from './api'

export type PromotionLifecycleStatus = 'inactive' | 'current' | 'upcoming' | 'expired'

export type PromotionStateFilter = 'all' | 'active' | 'inactive' | 'current' | 'upcoming' | 'expired'

export interface PromotionResponse {
  id: string
  tenantId: string
  name: string
  description: string | null
  discountPct: number | null
  discountAmt: number | null
  productId: string | null
  validFrom: string
  validUntil: string
  active: boolean
  lifecycleStatus: PromotionLifecycleStatus
  createdAt: string
  updatedAt: string
}

export interface PromotionPagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface PromotionsResponse {
  promotions: PromotionResponse[]
  pagination: PromotionPagination
}

export interface PromotionFilters {
  state?: PromotionStateFilter
  search?: string
  productId?: string | null
  page?: number
  limit?: number
}

export interface PromotionCreateInput {
  name: string
  description?: string | null
  discountPct?: number | null
  discountAmt?: number | null
  productId?: string | null
  validFrom: string
  validUntil: string
  active?: boolean
}

export interface PromotionUpdateInput {
  name?: string
  description?: string | null
  discountPct?: number | null
  discountAmt?: number | null
  productId?: string | null
  validFrom?: string
  validUntil?: string
  active?: boolean
}

export interface PromotionValidationDetail {
  field: string
  message: string
}

export interface ApiErrorLike {
  response?: {
    data?: {
      error?: string
      details?: PromotionValidationDetail[]
    }
  }
  message?: string
}

function buildQuery(params: Record<string, string | number | undefined | null>) {
  const search = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }

  return search.toString()
}

function normalizePromotion(promotion: any): PromotionResponse {
  return {
    ...promotion,
    description: promotion.description ?? null,
    discountPct: promotion.discountPct ?? null,
    discountAmt: promotion.discountAmt ?? null,
    productId: promotion.productId ?? null,
    lifecycleStatus: promotion.lifecycleStatus ?? 'inactive',
  }
}

export const promotionsApi = {
  async getPromotions(filters: PromotionFilters = {}): Promise<PromotionsResponse> {
    const query = buildQuery({
      state: filters.state,
      search: filters.search,
      productId: filters.productId,
      page: filters.page,
      limit: filters.limit,
    })

    const response = await api.get(`/promotions${query ? `?${query}` : ''}`)

    return {
      promotions: (response.data.data as any[]).map(normalizePromotion),
      pagination: response.data.pagination,
    }
  },

  async getPromotion(id: string): Promise<PromotionResponse> {
    const response = await api.get(`/promotions/${id}`)
    return normalizePromotion(response.data.data)
  },

  async createPromotion(payload: PromotionCreateInput): Promise<PromotionResponse> {
    const response = await api.post('/promotions', payload)
    return normalizePromotion(response.data.data)
  },

  async updatePromotion(id: string, payload: PromotionUpdateInput): Promise<PromotionResponse> {
    const response = await api.patch(`/promotions/${id}`, payload)
    return normalizePromotion(response.data.data)
  },

  async togglePromotion(id: string): Promise<PromotionResponse> {
    const response = await api.patch(`/promotions/${id}/toggle`)
    return normalizePromotion(response.data.data)
  },

  async deletePromotion(id: string): Promise<PromotionResponse> {
    const response = await api.delete(`/promotions/${id}`)
    return normalizePromotion(response.data.data)
  },
}

export default promotionsApi
